/**
 * Formateador de campos personalizados de productos.
 *
 * Las categorías de inventario (`catinventario`) tienen un JSON `fields` que define
 * los campos personalizados de los productos de esa categoría (ej. chasis, VIN, color).
 * El producto (`productos.campos`) guarda los valores de esos campos.
 *
 * Para serializados estos campos son la identidad del item — sin ellos no se sabe
 * qué unidad física se está transfiriendo.
 */

const parse = (v) => {
  if (!v) return null;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return null; }
};

/**
 * Devuelve un array de { id, name, value } con los campos del producto,
 * ignorando campos estándar y entradas vacías.
 */
export function extraerCampos(producto) {
  const fieldsDef = parse(producto?.categoria_fields || producto?.producto_categoria_fields) || [];
  const valores = parse(producto?.producto_campos || producto?.campos) || {};
  if (!Array.isArray(fieldsDef)) return [];
  return fieldsDef
    .map((f) => {
      const id = f?.id;
      if (id === undefined || id === null) return null;
      const val = valores[id];
      if (val === undefined || val === null || val === '') return null;
      const name = f?.name || f?.alias || String(id);
      return { id, name, value: String(val) };
    })
    .filter(Boolean);
}

/**
 * Devuelve el valor de un campo específico de la categoría (por id).
 */
export function valorCampo(producto, fieldId = 'id') {
  return extraerCampos(producto).find((c) => String(c.id) === String(fieldId))?.value || null;
}

export function esSerializado(producto) {
  return (producto?.categoria_tipo || producto?.tipo) === 'serializado';
}

/**
 * Etiqueta principal de búsqueda/display. Para serializados usa el campo `id`
 * (chasis/serial/VIN) que es el identificador único de esa unidad; para
 * genéricos usa el nombre del producto.
 */
export function nombreBusqueda(producto) {
  if (!producto) return '';
  if (esSerializado(producto)) {
    return valorCampo(producto, 'id') || producto.nombre || '';
  }
  return producto.nombre || '';
}

/**
 * Resumen en una línea de los campos (excluyendo los que se pasen en `excluir`).
 * Para serializados pasamos `excluir=['id']` cuando ya mostramos el id como label
 * primario, para no duplicarlo.
 */
export function resumenCampos(producto, max = 3, excluir = []) {
  const exclSet = new Set(excluir.map(String));
  const campos = extraerCampos(producto)
    .filter((c) => !exclSet.has(String(c.id)))
    .slice(0, max);
  if (campos.length === 0) return '';
  return campos.map((c) => `${c.name}: ${c.value}`).join(' · ');
}
